import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import Spacer from "../../../../../components/Spacer";
import { CheckboxGroup } from "../../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../../hooks";

import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../../Components";

// Extract keys dynamically from the schemas to stay in sync with schema changes
const getSchemaKeys = <T extends z.ZodObject<z.ZodRawShape>>(schema: T): Array<keyof z.infer<T>> => {
	return Object.keys(schema.shape) as Array<keyof z.infer<T>>;
};

const AIInsightsReviewsSettingsTab = () => {
	const formSchema = AiSettingsSchema.shape.reviewCycleInsightsSettings.and(
		AiSettingsSchema.shape.reviewSummarizationSettings
	);

	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isReviewCycleInsightsAssistantEnabled = data?.reviewCycleInsightsEnabled ?? true;
	const isInsightsCompleteNotificationEnabled =
		data?.reviewCycleInsightsSettings?.summaryCompleteNotification ?? false;
	const isReviewInsightsAssistantEnabled = data?.reviewSummarizationEnabled ?? true;

	// toggle handlers
	const handleReviewCycleInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ reviewCycleInsightsEnabled: val });
		toast.success(`Review Cycle Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleInsightsCompleteNotificationToggle = async (val: boolean) => {
		if (!data?.reviewCycleInsightsSettings) return;

		const updatedSettings = {
			...data?.reviewCycleInsightsSettings,
			summaryCompleteNotification: val,
		};
		await updateAiSettings({ reviewCycleInsightsSettings: updatedSettings });
		toast.success(`Insights Complete Notification ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleReviewInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ reviewSummarizationEnabled: val });
		toast.success(`Review Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({ value, resetForm }: { value: z.infer<typeof formSchema>; resetForm: () => void }) => {
		try {
			// Reconstruct nested structure from flat form value
			const reviewCycleInsightsSettingsKeys = getSchemaKeys(AiSettingsSchema.shape.reviewCycleInsightsSettings);
			const reviewSummarizationSettingsKeys = getSchemaKeys(AiSettingsSchema.shape.reviewSummarizationSettings);

			const reviewCycleInsightsSettings = Object.fromEntries(
				reviewCycleInsightsSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.reviewCycleInsightsSettings>;

			const reviewSummarizationSettings = Object.fromEntries(
				reviewSummarizationSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.reviewSummarizationSettings>;

			await updateAiSettings({ reviewCycleInsightsSettings, reviewSummarizationSettings });
			toast.success("Your changes have been saved.");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings.");
		}
	};

	// Checkbox options for the Review Cycle Insights Assistant
	const reviewCycleInsightsAssistantCheckBoxOptions = [
		{
			value: "reviewToneDistribution",
			label: "Review Tone Distribution",
			dataTestId: "review-tone-distribution-checkbox",
		},
		{ value: "highlightedReviews", label: "Highlighted Reviews", dataTestId: "highlighted-reviews-checkbox" },
		{
			value: "keyStrengthsAndOpportunities",
			label: "Key Strengths and Opportunities",
			dataTestId: "key-strengths-and-opportunities-checkbox",
		},
		{
			value: "commonThemesPositiveReviews",
			label: "Common Themes for Positive Reviews",
			dataTestId: "common-themes-positive-reviews-checkbox",
		},
		{
			value: "commonThemesNegativeReviews",
			label: "Common Themes for Negative Reviews",
			dataTestId: "common-themes-negative-reviews-checkbox",
		},
		{
			value: "recurringActionItems",
			label: "Recurring Action Items",
			dataTestId: "recurring-action-items-checkbox",
		},
		{ value: "disconnectsAnalysis", label: "Disconnects Analysis", dataTestId: "disconnects-analysis-checkbox" },
	];

	// Checkbox options for the Review Insights Assistant
	const reviewInsightsAssistantCheckBoxOptions = [
		{ value: "strengths", label: "Key Strengths and Opportunities", dataTestId: "strengths-checkbox" },
		{ value: "quotes", label: "Relevant Quotes from the Review", dataTestId: "quotes-checkbox" },
		{ value: "actions", label: "Action Items Mentioned by Reviewers", dataTestId: "actions-checkbox" },
		{
			value: "tone",
			label: "Overall Tone of the Review (Positive, Negative, Neutral)",
			dataTestId: "tone-checkbox",
		},
		{ value: "opinions", label: "Differences of Opinions Between Reviewers", dataTestId: "opinions-checkbox" },
	];

	const defaultValues = useAiSettingsDefaults({
		schema: formSchema,
		data,
		extractDefaults: (allDefaults) => ({
			...allDefaults.reviewCycleInsightsSettings,
			...allDefaults.reviewSummarizationSettings,
		}),
		extractData: (data) => ({
			...data.reviewCycleInsightsSettings,
			...data.reviewSummarizationSettings,
		}),
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="ai-insights-reviews-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={formSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="ai-insights-reviews-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="review-cycle-insights-section"
				title="Review Cycle Insights Assistant"
				description="The Review Cycle Insights Assistant allows admins to generate insights about all the reviews within a review cycle."
			>
				<SettingsToggle
					key="review-cycle-insights-toggle"
					id="enable-review-cycle-insights-assistant"
					label="Enable Review Cycle Insights Assistant"
					description="Disabling this setting will hide the ability for admins to generate insights about a review cycle."
					checked={isReviewCycleInsightsAssistantEnabled}
					onCheckedChange={handleReviewCycleInsightsAssistantToggle}
					dataTestId="review-cycle-insights-assistant-toggle"
				/>

				<SettingsToggle
					key="insights-complete-notification-toggle"
					id="enable-insights-complete-notification"
					label="Insights Complete Notification"
					description="Disabling this setting prevents the user who requested the insight from getting an email notification once it's finished processing."
					checked={isInsightsCompleteNotificationEnabled}
					onCheckedChange={handleInsightsCompleteNotificationToggle}
					dataTestId="insights-complete-notification-toggle"
				/>

				<CheckboxGroup
					key="review-cycle-insights-content-checkboxes"
					title="Insights Content"
					description="Control each section of content inside your review cycle insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the review cycle."
					options={reviewCycleInsightsAssistantCheckBoxOptions}
				/>
			</SectionLayout>

			<Spacer key="spacer-1" height={5} />
			<Divider key="divider" />
			<Spacer key="spacer-2" height={4} />

			<SectionLayout
				key="review-insights-section"
				title="Review Insights Assistant"
				description="The Review Insights Assistant allows users to generate insights about all the forms within a review."
			>
				<SettingsToggle
					key="review-insights-toggle"
					id="enable-review-insights-assistant"
					label="Enable Review Insights Assistant"
					description="Disabling this setting will hide the Review Insights Assistant from admins, managers, and contributors."
					checked={isReviewInsightsAssistantEnabled}
					onCheckedChange={handleReviewInsightsAssistantToggle}
					labelMaxWidth="max-w-[305px]"
					dataTestId="review-insights-assistant-toggle"
				/>

				<CheckboxGroup
					key="review-insights-content-checkboxes"
					title="Insights Content"
					description="Control each section of content inside your review insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the review."
					options={reviewInsightsAssistantCheckBoxOptions}
				/>
			</SectionLayout>

			<SettingsFormFooter
				key="reviews-settings-footer"
				formId="ai-insights-reviews-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default AIInsightsReviewsSettingsTab;
